import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import useUnsavedChanges from 'sharedHooks/useUnsavedChanges';
import UnSavedChangesModal from './LimitConfigurationBeta/components/UnSavedChangesModal';

import { GeneralTabsScrollable, LoadingIndicator } from 'sharedComponents';
import { getTabOptions, handleNavigationFromNotificationV2, isSmSeAsset } from './helpersV2';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import featureConfigHelper from 'helpers/featureConfigHelper';
import { OVER_ALL_STATUS, driveAnomalyTabs, EMAIL_SUB_TAB_ANOMALY } from './ConditionInsights/constants';
import { setActiveTab, setDriveAnomalyActiveTab } from './ConditionInsights/actions';
import { conditionInsightsTabs } from './ConditionInsights/helpers';
import { setIsDefaultLimitsSelected } from './OperationalParametersV2/SingleChartView/Trend/LimitSelector/actions';
import { getAssetDetailsV2, setActiveTabV2 } from './actions';
import { triggerReportIssueDialog } from '../../PowertrainOverview/actions';
import { cancel as cancelLimitChanges } from './LimitConfigurationBeta/actions';
import { apiCallStatus } from 'helpers/constants';
import './style.scss';
import { tabIDs } from './constants';
import { setSelectedNotification } from '../../PowertrainOverview/Header/NotificationDropdown/actions';
import { useQueryParams } from 'sharedHooks/useQuery';
import { handleRemoveQueryParams } from '../../PowertrainConditionMonitoring/ConditionIndicesOverviewV2/helpers';
import { trackVirtualPageView } from 'helpers/clarityHelper';
const TabsV2 = ({ selectedAsset, selectedAssetV2, newCapability, conditionBasedClassName }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { assetId, tabId, subTab, subTab2 } = useQueryParams();
    const allAssetsV2 = useSelector(({ generalFilter }) => generalFilter?.assets?.data || []);
    const allAssetsV2LoadingStatus = useSelector(({ generalFilter }) => generalFilter?.assets?.loadingStatus);
    const activeTabV2 = useSelector(({ detailedInformation }) => detailedInformation?.activeTabV2);
    const newCapabilityLoadingStatus = useSelector(({ powertrainAssetDetails }) => powertrainAssetDetails?.newAssetCapabilityV2?.loadingStatus);
    const selectedNotification = useSelector(({ notificationPanel }) => notificationPanel?.selectedNotification);
    const organizationID = useSelector(({ generalFilter }) => generalFilter?.selectedFilters?.organizations[0]?.organizationID);
    const activeOPTab = useSelector(({ operationalParameters }) => operationalParameters?.activeTab);
    const multiChart = useSelector(({ operationalParameters }) => operationalParameters?.multiChart);
    const hasCustomSignalProfile = useSelector(({ detailedInformation }) => detailedInformation?.assetDetails?.data?.hasCustomSignalProfile);

    // Check for unsaved changes in limit configuration
    const hasUnsavedLimitChanges = useSelector(({ limitConfig }) =>
        limitConfig?.currentTimeSeries?.some(item =>
            item?.isModified || item?.limits?.some(limit => limit?.isModified)
        ) || false
    );
    const { t: translate } = useTranslation();

    // Initialize unsaved changes hook
    const {
        showUnsavedModal,
        setShowUnsavedModal,
        checkUnsavedChanges,
        handleDiscardAndContinue: originalHandleDiscardAndContinue,
    } = useUnsavedChanges(hasUnsavedLimitChanges);

    // Custom discard handler that resets limit config state
    const handleDiscardAndContinue = () => {
        dispatch(cancelLimitChanges());
        originalHandleDiscardAndContinue();
    };
    const featureConfig = new featureConfigHelper();
    const assetCapabilities = useMemo(() => ({
        isEventLogHidden: !newCapability.hasEventListCapability(),
        isAdvancedAnalyticsDisabled: !newCapability.hasCBMCapability(),
        isOperationalParameterV2Hidden: !newCapability.hasOpCapability() || !newCapability.hasTrendCapability(),
        isLimitConfigHidden: !newCapability.hasLimitConfigCapability(),
        hasConditionIndexNewCapability: !newCapability.hasConditionIndexCapability(),
        hasConditionDetailsCapability: !newCapability.hasConditionDetailsNewCapability(),
        hasAdvancedAnalyticsV2Capability: !newCapability.hasAdvancedAnalyticsV2Capability(),
        isCBMFeatureEnabled: featureConfig.isCBMFeatureEnabled(),
        isRegreasingAdviceDisabled: !newCapability.hasRegreasingAdviceCapability(),
        isMVDrive: newCapability.hasMVDriveCapability(),
        isDeionizerMaintenance: newCapability.hasDeionizerMaintenanceCapability(),
        isACS6080ConditionBasedMaintenance: !newCapability.hasACS6080ConditionBasedMaintenanceCapability(), /* ACS6080 Condition Based Maintenance capability */
        iotPanelDriveConditionBasedMaintenance: newCapability.hasIotPanelDriveConditionBasedMaintenance(),
    }), [newCapability]);

    const [tabOptions, setTabOptions] = useState({});
    const [isFullyLoaded, setIsFullyLoaded] = useState(false);

    const handleReportIssue = () => {
        dispatch(triggerReportIssueDialog());
    };

    useEffect(() => {
        if (selectedAsset && selectedAsset?.id) {
            dispatch(getAssetDetailsV2(selectedAsset?.id));
        }
    }, [selectedAsset?.id]);

    useEffect(() => {
        trackVirtualPageView(activeTabV2);
    }, [activeTabV2]);

    // Builds/sets the tab options for the currently selected asset, once the asset list has loaded.
    const applyTabOptionsForAsset = () => {
        if (!(allAssetsV2?.length > 0 && selectedAssetV2)) { return; }
        const assetDetail = { ...allAssetsV2?.find(asset => asset?.id === selectedAsset?.id), hasCustomSignalProfile };
        const options = getTabOptions(translate, isSmSeAsset(selectedAsset), OVER_ALL_STATUS[selectedAssetV2?.condition], assetCapabilities, assetDetail, selectedAssetV2, handleReportIssue);
        setTabOptions(options);
        dispatch(setActiveTabV2(options[activeTabV2]?.hidden === false ? activeTabV2 : tabIDs.summary));
        setIsFullyLoaded(true);
    };

    /* Handle inner ConditionInsights tab for anomaly detection notifications */
    const applyConditionInsightsNotificationTab = (conditionType, conditionName) => {
        if (conditionType !== conditionInsightsTabs.ANOMALY_DETECTION) {
            /* Non-anomaly condition notifications always show Condition Indices */
            dispatch(setActiveTab(conditionInsightsTabs.CONDITION_INDICES));
            return;
        }
        dispatch(setActiveTab(conditionInsightsTabs.ANOMALY_DETECTION));
        const normalizedConditionName = conditionName?.toLowerCase();
        if (normalizedConditionName?.includes('energy')) {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.ENERGY));
        } else if (normalizedConditionName?.includes('cooling')) {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.COOLING));
        }
    };

    /* Handle inner ConditionInsights sub-tab/KPI selection from email deep-link query params */
    const applyConditionInsightsEmailSubTab = (subTabParam, subTab2Param) => {
        if (subTabParam?.toLowerCase() !== EMAIL_SUB_TAB_ANOMALY) {
            dispatch(setActiveTab(conditionInsightsTabs.CONDITION_INDICES));
            return;
        }
        dispatch(setActiveTab(conditionInsightsTabs.ANOMALY_DETECTION));
        const normalizedSubTab2 = subTab2Param?.toLowerCase();
        if (normalizedSubTab2 === driveAnomalyTabs.ENERGY) {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.ENERGY));
        } else if (normalizedSubTab2 === driveAnomalyTabs.COOLING) {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.COOLING));
        }
    };

    /* HANDLE NOTIFICATION FROM PANEL */
    const applyNotificationFromPanel = () => {
        if (!selectedNotification?.assetId) { return; }
        const tab = handleNavigationFromNotificationV2(selectedNotification, history);
        dispatch(setActiveTabV2(tab || tabIDs.summary));
        if (tab === tabIDs.conditionInsights) {
            applyConditionInsightsNotificationTab(
                selectedNotification?.properties?.conditionType,
                selectedNotification?.properties?.conditionName
            );
        }
    };

    /* HANDLE NOTIFICATION FROM EMAIL */
    const applyNotificationFromEmail = () => {
        if (!(assetId && assetId !== '' && tabId && tabId !== '')) { return; }
        dispatch(setActiveTabV2(tabId || tabIDs.summary));
        if (tabId === tabIDs.conditionInsights && subTab && subTab !== '') {
            applyConditionInsightsEmailSubTab(subTab, subTab2);
        }
    };

    useEffect(() => {
        //Removing old capabilities check for now
        if (!(newCapability.isSet() && allAssetsV2LoadingStatus === apiCallStatus.SUCCESS)) { return; }

        applyTabOptionsForAsset();
        applyNotificationFromPanel();
        applyNotificationFromEmail();
    }, [newCapability, selectedAsset, assetCapabilities, translate, selectedAssetV2, allAssetsV2LoadingStatus, selectedNotification, organizationID, hasCustomSignalProfile, tabId, subTab, subTab2]);

    useEffect(() => {
        if (Object.keys(tabOptions).length > 0) {
            dispatch(setIsDefaultLimitsSelected(false));
        }
    }, [tabOptions, setActiveTabV2]);

    // Fallback while loading tabs
    if (!isFullyLoaded) {
        return <LoadingIndicator />;
    }

    const hasTags = document.getElementsByClassName('asset-overview-v2__tag')?.length > 0;
    const hasNotification = document.getElementsByClassName('notification-row')?.length > 0 || document.getElementsByClassName('notification-status-bar')?.length > 0;
    return (
        <LoadingStatusWrapper loadingStatus={[newCapabilityLoadingStatus]}>
            {showUnsavedModal &&
                <UnSavedChangesModal
                    showModal={showUnsavedModal}
                    setShowModal={setShowUnsavedModal}
                    handleDiscardAndContinue={handleDiscardAndContinue}
                    isResetToDefault={false}
                />
            }
            {newCapability.isSet() &&
                <div className='asset-detail-tabs-v2' id='asset-detail-tabs-v2'>
                    <GeneralTabsScrollable
                        options={tabOptions}
                        variant='panel'
                        defaultTab={activeTabV2 || tabIDs.summary}
                        activeTab={activeTabV2}
                        activeOPTab={activeOPTab}
                        multiChart={multiChart}
                        showScrollbar={false}
                        handleSelectedTab={(tabKey) => {
                            // Check if we're already on the target tab
                            if (tabKey === activeTabV2) {
                                return true;
                            }
                            // If there are unsaved changes, show modal but don't change tab yet
                            if (hasUnsavedLimitChanges) {
                                const performTabSwitch = () => {
                                    dispatch(setActiveTabV2(tabKey));
                                    dispatch(setSelectedNotification(null));
                                };
                                checkUnsavedChanges(performTabSwitch);
                                return false; // Prevent tab change
                            }
                            handleRemoveQueryParams(history);
                            dispatch(setActiveTabV2(tabKey));
                            dispatch(setSelectedNotification(null));
                            history.push({
                                pathname: history?.location?.pathname,
                                search: new URLSearchParams({ tabId: tabKey }).toString()
                            });
                            return true; // Allow tab change
                        }}
                        parentElementId='asset-detail-tabs-v2'
                        conditionBasedClassName={conditionBasedClassName}
                        tabContentClass={`${hasTags ? 'tab-content-with-tags' : ''} ${hasNotification ? 'tab-content-with-notification' : ''}`}
                    />
                </div>
            }
        </LoadingStatusWrapper>
    );
};

TabsV2.propTypes = {
    selectedAsset: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    selectedAssetV2: PropTypes.shape({
        condition: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    newCapability: PropTypes.object,
    conditionBasedClassName: PropTypes.string
};

export default TabsV2;
